/**
 * @fileoverview Arquivo responsável por registrar as rotas da aplicação e compor seus providers principais.
 */

import React, { useContext, useEffect, useState } from "react";
import { BrowserRouter, Switch, Route as RouterRoute, Redirect } from "react-router-dom";
import { ToastContainer } from "react-toastify";

import LoggedInLayout from "../layout";
import Dashboard from "../pages/Dashboard/";
import TicketResponsiveContainer from "../pages/TicketResponsiveContainer";
import Signup from "../pages/Signup/";
import Login from "../pages/Login/";
import Connections from "../pages/Connections/";
import SettingsCustom from "../pages/SettingsCustom/";
import Users from "../pages/Users";
import Contacts from "../pages/Contacts/";
import Queues from "../pages/Queues/";
import Tags from "../pages/Tags/";
import Helps from "../pages/Helps/";
import Companies from "../pages/Companies/";
import QuickMessages from "../pages/QuickMessages/";
import { AuthProvider, AuthContext } from "../context/Auth/AuthContext";
import { getHomeRoute } from "../utils/accessScope";
import { NAV_ACCESS, multiUnitPermission } from "../layout/navigation";
import usePermission, { isAdminUser } from "../hooks/usePermission";
import api from "../services/api";
import BackdropLoading from "../components/BackdropLoading";
import { TicketsContextProvider } from "../context/Tickets/TicketsContext";
import { WhatsAppsProvider } from "../context/WhatsApp/WhatsAppsContext";
import Route from "./Route";
import Annoucements from "../pages/Annoucements";
import ToDoList from "../pages/ToDoList/";
import Files from "../pages/Files/";
import ForgotPassword from "../pages/ForgotPassword/";
import ResetPassword from "../pages/ResetPassword/";
import VerifyEmail from "../pages/VerifyEmail/";
import SuperDashboard from "../pages/SuperDashboard";
import SuperAdminPanel from "../pages/SuperAdminPanel";
import ExternalLeads from "../pages/ExternalLeads";
import Flows from "../pages/Flows";
import AiSetup from "../pages/AiSetup";
import CompanySetup from "../pages/CompanySetup";
import SetupSteps from "../pages/CompanySetup/SetupSteps";
import Invite from "../pages/Invite";
import { isOnboardingSession } from "../utils/decodeJwtPayload";
import FlowBuilder from "../pages/Flows/FlowBuilder";
import FlowResults from "../pages/Flows/FlowResults";
import InternalChat from "../pages/InternalChat";
import InternalChatInvite from "../pages/InternalChat/Invite";
import NewsPosts from "../pages/NewsPosts";
import MetaTemplates from "../pages/MetaTemplates";
import Integrations from "../pages/Integrations";
import WorkOrders from "../pages/WorkOrders";
import SmartApproaches from "../pages/SmartApproaches";
import MultiUnit from "../pages/MultiUnit";
import Academy from "../pages/Academy";
import AcademyCourse from "../pages/Academy/Course";
import AcademyManage from "../pages/AcademyManage";

/**
 * Redirect da raiz sensível ao escopo. Precisa ser componente (e não um
 * <Redirect> cru) para poder ler o AuthContext — está dentro do AuthProvider.
 */
const HomeRedirect = () => {
  const { user, isAuth } = useContext(AuthContext);
  // Logado, a tela inicial depende do acesso da pessoa (com a regra nova, das
  // permissões): espera ele chegar.
  const { loaded } = usePermission();
  // A esteira de cadastro em aberto é a tela inicial de quem criou a Empresa
  // (um administrador), até o "Ir para o painel": volta no passo guardado.
  const mayHaveSetup = isAuth && isAdminUser(user) && !user.super;
  const [setupOpen, setSetupOpen] = useState(null);
  useEffect(() => {
    if (!mayHaveSetup) {
      setSetupOpen(false);
      return undefined;
    }
    let alive = true;
    api
      .get("/onboarding/setup")
      .then(({ data }) => alive && setSetupOpen(data?.stage === "steps"))
      .catch(() => alive && setSetupOpen(false));
    return () => {
      alive = false;
    };
  }, [mayHaveSetup]);
  if (isAuth && (!loaded || setupOpen === null)) return <BackdropLoading />;
  if (isAuth && setupOpen) return <Redirect to="/configurar" />;
  return <Redirect to={getHomeRoute(user)} />;
};

// Quem entrou e ainda não tem conta (a sessão do onboarding) só tem a esteira:
// o app pediria a conta em todas as telas e chamadas. O token guardado já diz
// que é essa sessão: o app nem monta enquanto ela é relida (sem espera para os
// outros).
const OnboardingGate = ({ children }) => {
  const { isAuth, user } = useContext(AuthContext);
  if (isOnboardingSession() || (isAuth && user?.onboarding)) return <Redirect to="/comecar" />;
  return children;
};

const Routes = () => {
  return (
    <BrowserRouter>
      <AuthProvider>
        <TicketsContextProvider>
          <Switch>
            <Route exact path="/login" component={Login} />
            <Route exact path="/signup" component={Signup} />
            <Route exact path="/forgot-password" component={ForgotPassword} />
            <Route exact path="/reset-password" component={ResetPassword} />
            {/* Confirmar o e-mail abre logado ou não: o link pode ser clicado com a sessão aberta. */}
            <RouterRoute exact path="/verify-email" component={VerifyEmail} />
            <RouterRoute exact path="/forgetpsw"><Redirect to="/forgot-password" /></RouterRoute>
            <RouterRoute exact path="/internal-chat/invite/:token" component={InternalChatInvite} />
            {/* O começo da esteira de cadastro (Empresa e conta), fora do app: ainda não há conta. */}
            <RouterRoute exact path="/comecar" component={CompanySetup} />
            {/* O convite da equipe abre logado ou não, e também na sessão do onboarding. */}
            <RouterRoute exact path="/invite" component={Invite} />
            {/* <Route exact path="/create-company" component={Companies} /> */}
            <OnboardingGate>
            <WhatsAppsProvider>
              <LoggedInLayout>
                {/* Raiz nao abre o Dashboard: ele dispara 13 requisicoes no
                    mount (12 em /dashboard*) e nao deve ser carregado por quem
                    so abriu o link raiz. Manda pra home DO ESCOPO — atendimento
                    vai pra /tickets, "Apenas Interno" vai pro chat interno.
                    Alinha com o login, que usa o mesmo getHomeRoute. */}
                <RouterRoute exact path="/">
                  <HomeRedirect />
                </RouterRoute>
                <Route exact path="/dashboard" component={Dashboard} requiresAttendance permission="dashboard:view" catalog="reports.view" also={NAV_ACCESS.dashboard[2]} isPrivate />
                {/* Rota legada: o Dashboard Geral virou os gráficos do Multi
                    unidades. Redireciona para preservar bookmarks existentes. */}
                <RouterRoute exact path="/dashboard-geral">
                  <Redirect to="/multi-unidades" />
                </RouterRoute>
                <Route
                  exact
                  path="/super-dashboard"
                  component={SuperDashboard}
                  isPrivate
                />
                <Route
                  exact
                  path="/super-admin-panel"
                  component={SuperAdminPanel}
                  isPrivate
                />
                <Route
                  exact
                  path="/tickets/:ticketId?"
                  component={TicketResponsiveContainer} requiresAttendance
                  catalog="tickets.view"
                  isPrivate
                />
                <Route
                  exact
                  path="/connections"
                  component={Connections} requiresAttendance
                  catalog="connections.manage"
                  isPrivate
                />
                <Route
                  exact
                  path="/quick-messages"
                  component={QuickMessages} requiresAttendance
                  catalog="tickets.view"
                  isPrivate
                />
                <Route exact path="/todolist" component={ToDoList} isPrivate />
                <Route exact path="/tags" component={Tags} requiresAttendance catalog="tickets.view" isPrivate />
                <Route exact path="/contacts" component={Contacts} requiresAttendance catalog="tickets.view" isPrivate />
                <Route exact path="/helps" component={Helps} isPrivate />
                <Route exact path="/users" component={Users} catalog="team.view" isPrivate />
                <Route exact path="/files" component={Files} catalog="files.manage" isPrivate />
                <Route
                  exact
                  path="/settings/:section?"
                  component={SettingsCustom}
                  isPrivate
                />
                <Route
                  exact
                  path="/companies"
                  component={Companies}
                  isPrivate
                />
                <Route exact path="/queues" component={Queues} requiresAttendance catalog="queues.manage" isPrivate />
                <Route
                  exact
                  path="/announcements"
                  component={Annoucements}
                  isPrivate
                />
                <Route
                  exact
                  path="/crm"
                  component={ExternalLeads}
                  catalog="crm.use"
                  isPrivate
                />
                <Route exact path="/fluxo" component={Flows} requiresAttendance permission="drawer-admin-items:view" catalog="flows.view" isPrivate />
                <Route exact path="/assistente-ia" component={AiSetup} requiresAttendance permission="drawer-admin-items:view" catalog="ai_agents.manage" isPrivate />
                {/* Os passos da esteira depois da conta (IA, WhatsApp, equipe): só do criador da Empresa, enquanto ela está aberta. */}
                <Route exact path="/configurar" component={SetupSteps} isPrivate />
                <Route exact path="/fluxo/:id" component={FlowBuilder} requiresAttendance permission="drawer-admin-items:view" catalog="flows.view" isPrivate />
                <Route
                  exact
                  path="/fluxo/:id/resultados"
                  component={FlowResults}
                  requiresAttendance
                  permission="drawer-admin-items:view"
                  catalog="flows.view"
                  isPrivate
                />
                <Route exact path="/integrations" component={Integrations} requiresAttendance catalog="integrations.manage" isPrivate />
                <Route exact path="/work-orders" component={WorkOrders} requiresAttendance catalog="work_orders.view" isPrivate />
                <Route exact path="/smart-approaches" component={SmartApproaches} requiresAttendance permission="drawer-admin-items:view" catalog="smart_approaches.view" isPrivate />
                <Route exact path="/internal-chat/:channelId?" component={InternalChat} catalog="internal_chat.use" isPrivate />
                <Route exact path="/news-posts" component={NewsPosts} isPrivate />
                <Route
                  exact
                  path="/meta-templates"
                  component={MetaTemplates} requiresAttendance
                  permission="drawer-admin-items:view"
                  catalog="templates.manage"
                  isPrivate
                />
                <Route
                  exact
                  path="/multi-unidades"
                  component={MultiUnit}
                  requiresAttendance
                  permission="dashboard:view"
                  catalog={multiUnitPermission}
                  isPrivate
                />
                <Route exact path="/academy" component={Academy} isPrivate />
                <Route
                  exact
                  path="/academy/courses/:id"
                  component={AcademyCourse}
                  isPrivate
                />
                <Route
                  exact
                  path="/academy/manage"
                  component={AcademyManage}
                  isPrivate
                />
              </LoggedInLayout>
            </WhatsAppsProvider>
            </OnboardingGate>
          </Switch>
          <ToastContainer
            autoClose={5000}
            hideProgressBar
            newestOnTop
            closeOnClick
            pauseOnHover={false}
            pauseOnFocusLoss={false}
            draggable={false}
          />
        </TicketsContextProvider>
      </AuthProvider>
    </BrowserRouter>
  );
};

export default Routes;
